/**
 * Invoice list filters (design §B) as pure functions, so the date maths and
 * the CSV shape are testable without rendering. The UI keeps a small
 * ListFilters object; toListQuery turns it into the GET /invoices query
 * using the shop's "today" (Asia/Colombo), matching the server's notion of
 * overdue.
 */
import type { InvoiceListItem, ListQuery } from '../../internals/repository/InvoiceRepository';
import { STATUS } from './components/StatusChip';
import { addDays, date, money } from './format';

export type TabKey = 'ALL' | 'DRAFT' | 'UNPAID' | 'OVERDUE' | 'PARTLY_PAID' | 'PAID' | 'VOID';
export type IssuedFilter = 'ALL' | 'MONTH' | 'LAST_30' | 'QUARTER';
export type DueFilter = 'ANY' | 'OVERDUE' | 'NEXT_7' | 'LATER';
export type AmountFilter = 'ANY' | 'LT_25K' | 'MID' | 'GT_100K';

export const TABS: { key: TabKey; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'DRAFT', label: 'Drafts' },
  { key: 'UNPAID', label: 'Unpaid' },
  { key: 'OVERDUE', label: 'Overdue' },
  { key: 'PARTLY_PAID', label: 'Partly paid' },
  { key: 'PAID', label: 'Paid' },
  { key: 'VOID', label: 'Void' },
];

export const ISSUED_OPTIONS: { value: IssuedFilter; label: string }[] = [
  { value: 'ALL', label: 'All time' },
  { value: 'MONTH', label: 'This month' },
  { value: 'LAST_30', label: 'Last 30 days' },
  { value: 'QUARTER', label: 'This quarter' },
];

export const DUE_OPTIONS: { value: DueFilter; label: string }[] = [
  { value: 'ANY', label: 'Any' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'NEXT_7', label: 'Next 7 days' },
  { value: 'LATER', label: 'Later' },
];

export const AMOUNT_OPTIONS: { value: AmountFilter; label: string }[] = [
  { value: 'ANY', label: 'Any' },
  { value: 'LT_25K', label: 'Under Rs. 25,000' },
  { value: 'MID', label: 'Rs. 25,000 to 100,000' },
  { value: 'GT_100K', label: 'Over Rs. 100,000' },
];

export interface ListFilters {
  tab: TabKey;
  q: string;
  issued: IssuedFilter;
  due: DueFilter;
  amount: AmountFilter;
}

export const EMPTY_FILTERS: ListFilters = { tab: 'ALL', q: '', issued: 'ALL', due: 'ANY', amount: 'ANY' };

/** True when search or any select narrows the list (tabs do not count). */
export const isFiltered = (f: ListFilters) =>
  !!f.q.trim() || f.issued !== 'ALL' || f.due !== 'ANY' || f.amount !== 'ANY';

/** First day of the calendar quarter containing `today` (YYYY-MM-DD). */
export function quarterStart(today: string): string {
  const [y, m] = today.split('-').map(Number);
  const qm = Math.floor((m - 1) / 3) * 3 + 1;
  return `${y}-${String(qm).padStart(2, '0')}-01`;
}

/** Tabs whose rows can be overdue; Due "Overdue" narrows these to the Overdue tab. */
const OVERDUE_CAPABLE: TabKey[] = ['ALL', 'UNPAID', 'OVERDUE'];

export function toListQuery(f: ListFilters, today: string): ListQuery {
  const query: ListQuery = { docType: 'INVOICE', tab: f.tab };
  const q = f.q.trim();
  if (q) query.q = q;

  if (f.issued === 'MONTH') query.from = `${today.slice(0, 7)}-01`;
  else if (f.issued === 'LAST_30') query.from = addDays(today, -30);
  else if (f.issued === 'QUARTER') query.from = quarterStart(today);

  if (f.due === 'OVERDUE') {
    // Overdue is a status (unpaid balance past due), not just a past due
    // date: the server's only status filter is the tab, so use it.
    if (OVERDUE_CAPABLE.includes(f.tab)) query.tab = 'OVERDUE';
    query.dueTo = addDays(today, -1);
  } else if (f.due === 'NEXT_7') {
    query.dueFrom = today;
    query.dueTo = addDays(today, 7);
  } else if (f.due === 'LATER') {
    query.dueFrom = addDays(today, 8);
  }

  // Server bounds are inclusive; "under 25k" and "over 100k" are strict.
  if (f.amount === 'LT_25K') query.maxTotalMinor = 2_500_000 - 1;
  else if (f.amount === 'MID') { query.minTotalMinor = 2_500_000; query.maxTotalMinor = 10_000_000; }
  else if (f.amount === 'GT_100K') query.minTotalMinor = 10_000_000 + 1;

  return query;
}

export const CSV_HEADER = ['Number', 'Customer', 'Company', 'Issued', 'Due', 'Total', 'Balance', 'Status'];

export function csvRows(items: InvoiceListItem[], currency = 'LKR'): string[][] {
  return items.map((it) => [
    it.number ?? 'Draft',
    it.customer?.name ?? '',
    it.customer?.companyName ?? '',
    date(it.issueDate),
    it.dueDate ? date(it.dueDate) : '',
    money(it.totalMinor, currency),
    money(it.balanceMinor, currency),
    STATUS[it.displayStatus]?.label ?? it.displayStatus,
  ]);
}

const cell = (v: string) => (/[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** RFC 4180 text with a header row; CRLF line ends so Excel opens it cleanly. */
export function toCsv(items: InvoiceListItem[], currency = 'LKR'): string {
  return [CSV_HEADER, ...csvRows(items, currency)].map((r) => r.map(cell).join(',')).join('\r\n');
}

/** Sum of positive balances, for the bulk bar ("Rs. X balance"). */
export const selectedBalance = (items: InvoiceListItem[]) =>
  items.reduce((a, it) => a + Math.max(0, it.balanceMinor || 0), 0);
